// RadiocheckToggle - shows/hides targets based on the checked state of radio/
// checkbox inputs carrying [data-radiocheck-toggle]. Behavioral port of v1
// modules/RadiocheckToggle.js.

import { escapeSelectorName } from '../utils';
import { TRIGGER_EVT, TOGGLE_EVT, type ToggleEventDetail } from './toggle';

export interface RadiocheckToggleConfig {
  toggleClass: string;
  settingsAttr: string;
}

const defaultConfig: RadiocheckToggleConfig = {
  toggleClass: 'hide',
  settingsAttr: 'data-radiocheck-toggle',
};

export default class RadiocheckToggle {
  selector: string;

  config: RadiocheckToggleConfig;

  triggers: HTMLInputElement[] = [];

  constructor(
    selector = '[data-radiocheck-toggle]',
    config: Partial<RadiocheckToggleConfig> = {},
  ) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  private _init(): void {
    this.triggers.push(
      ...document.querySelectorAll<HTMLInputElement>(this.selector),
    );
    this.triggers.forEach(trigger => {
      trigger.addEventListener('change', this._onChange);
    });
  }

  private _onChange = (e: Event): void => {
    const triggerEl = e.currentTarget as HTMLInputElement;

    // Re-evaluate every input in the same radio/checkbox group, so targets of
    // the un-checked siblings hide again.
    const siblings = [
      ...document.querySelectorAll<HTMLInputElement>(
        `[name=${escapeSelectorName(triggerEl.name)}]`,
      ),
    ];

    siblings.forEach(sibling => {
      this._getTargets(sibling).forEach(target => {
        this.doToggle(target, sibling);
        sibling.dispatchEvent(new CustomEvent(TRIGGER_EVT, { bubbles: true }));
      });
    });
  };

  destroy(): void {
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('change', this._onChange);
    });
    this.triggers = [];
  }

  update(): void {
    this.destroy();
    this._init();
  }

  doToggle(el: Element, triggerEl: HTMLInputElement): boolean {
    const toggleClass = el.getAttribute('data-toggle-class') || this.config.toggleClass;

    if (triggerEl.checked) {
      el.classList.remove(toggleClass);
    } else {
      el.classList.add(toggleClass);
    }

    el.dispatchEvent(
      new CustomEvent<ToggleEventDetail>(TOGGLE_EVT, {
        detail: {
          target: el,
          trigger: triggerEl,
        },
        bubbles: true,
      }),
    );
    return true;
  }

  private _getTargets(trigger: HTMLInputElement): Element[] {
    const targets = trigger.getAttribute(this.config.settingsAttr);
    if (!targets) {
      return [];
    }

    return targets
      .split(',')
      .map(selector => {
        const target = selector.trim();
        if (target === 'self') {
          return trigger;
        }
        if (target === 'parent') {
          const parentHop = parseInt(
            trigger.getAttribute('data-parent-hop') || '1',
            10,
          );
          let parentElement: Element | null = trigger;
          for (let j = 0; j < parentHop && parentElement; j++) {
            parentElement = parentElement.parentElement;
          }
          return parentElement;
        }
        if (target === 'previous') {
          return trigger.previousElementSibling;
        }
        if (target === 'next') {
          return trigger.nextElementSibling;
        }
        return document.querySelector(target);
      })
      .filter((el): el is Element => Boolean(el));
  }
}
